import { computed, unref, type ComputedRef } from 'vue'

import { useMutation, useQuery, useInfiniteQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'

import { queryClient } from '@/lib/queryClient'
import { useAuthStore } from '@/store/auth'
import { useGptStore } from '@/store/gpt'

import { handleApiErrorToast } from '..'
import { applyGptPinStateChange } from './gptCacheUtils'
import { adminQueryKeys } from './useAdminQueries'
import { GptApi } from '../modules/gpt'

import type { IPaginatedResponse } from '..'
import type {
  IGpt,
  IGptData,
  IGptListFilters,
  IGptListItem,
  IGptPinOrderBody,
} from '../types/gpt'
import type { AxiosError } from 'axios'

const ITEMS_PER_PAGE = 10

export const REMOVED_GPT_UUID = '00000000-0000-0000-0000-000000000000'

export const useGptQueries = () => {
  const { isAuthenticated } = storeToRefs(useAuthStore())

  const list = (filters?: Partial<IGptListFilters>) =>
    useQuery<IPaginatedResponse<IGptListItem>>({
      queryKey: ['gpts', filters],
      queryFn: ({ signal }) => GptApi.list(filters, { signal }),
      enabled: isAuthenticated,
    })

  /**
   * Query for GPT tabs, handles pagination
   * @param filters - Filters to apply to the GPT list query
   * @returns Infinite query for GPT list
   */
  const listInfinite = (filters: ComputedRef<IGptListFilters>) =>
    useInfiniteQuery<
      IPaginatedResponse<IGptListItem>,
      Error,
      IPaginatedResponse<IGptListItem>,
      readonly unknown[],
      number
    >({
      queryKey: computed(() => ['gpts', 'infinite', filters.value] as const),
      queryFn: ({ pageParam, signal }) =>
        GptApi.list(
          {
            ...filters.value,
            size: ITEMS_PER_PAGE,
            page: pageParam,
          },
          { signal },
        ),
      getNextPageParam: ({ page, pages }) =>
        page < pages ? page + 1 : undefined,
      initialPageParam: 1,
      enabled: isAuthenticated,
      select: (data) => {
        const lastPage = data.pages.at(-1) || {
          page: 1,
          pages: 0,
          size: 0,
          total: 0,
          items: [],
        }
        return {
          items: data.pages.flatMap((page) => page.items),
          page: lastPage.page,
          pages: lastPage.pages,
          size: lastPage.size,
          total: lastPage.total,
        }
      },
    })

  const get = (id?: ComputedRef<string | undefined>) => {
    return useQuery<IGpt, AxiosError, IGpt>({
      queryKey: computed(() => {
        const idValue = unref(id)
        return idValue ? ['gpts', 'get', idValue] : ['gpts', 'get']
      }),
      queryFn: () => {
        const idValue = unref(id)
        if (!idValue) {
          throw new Error('ID is required')
        }
        return GptApi.get(idValue)
      },
      enabled: computed(() => {
        const idValue = unref(id)
        return (
          isAuthenticated.value && !!idValue && idValue !== REMOVED_GPT_UUID
        )
      }),
    })
  }

  const create = useMutation<IGpt, Error, IGptData>({
    mutationFn: (data: IGptData) => GptApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpts'] })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
    },
  })

  const update = useMutation({
    mutationFn: (data: IGpt | Omit<IGpt, 'config'>) => GptApi.update(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['gpts', 'get', variables.id],
      })
      queryClient.invalidateQueries({ queryKey: ['gpts'] })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => GptApi.remove(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['gpts', 'get', variables],
      })
      queryClient.invalidateQueries({ queryKey: ['gpts'] })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
    },
  })

  const togglePin = useMutation({
    mutationFn: (id: string) => GptApi.togglePin(id),
    onSuccess: ({ pinned }, gptId) => {
      applyGptPinStateChange(gptId, pinned)
      useGptStore().patchGptPinned(gptId, pinned)
    },
  })

  const setPinOrder = useMutation({
    mutationFn: (body: IGptPinOrderBody) => GptApi.setPinOrder(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpts'] })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
    },
    onError: handleApiErrorToast,
  })

  const rate = useMutation({
    mutationFn: ({ gptId, rating }: { gptId: string; rating: number }) =>
      GptApi.rateGpt(gptId, { rating }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['gpts', 'get', variables.gptId],
      })
      queryClient.invalidateQueries({ queryKey: ['gpts'] })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
    },
  })

  return {
    list,
    listInfinite,
    get,
    create,
    update,
    remove,
    togglePin,
    setPinOrder,
    rate,
  }
}
